import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { fetchLatestPings } from "../lib/pings";
import { cx } from "../ui/ui";

function fmtTime(ts) {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function dayKey(ts) {
  const d = new Date(ts);
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}

function kindNice(kind) {
  return String(kind || "").replaceAll("_", " ");
}

function groupByDay(pings) {
  const map = new Map();
  for (const p of pings) {
    const k = dayKey(p.created_at);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(p);
  }
  return Array.from(map.entries());
}

export default function Feed({
  meId,
  myLabel = "Me",
  partnerLabel = "Partner",
  limit = 100,
  title = "Timeline",
  compact = false,
}) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");

  async function load() {
    setStatus("");
    const { data, error } = await fetchLatestPings(limit);
    if (error) return setStatus(error.message || "Failed to load pings.");
    setItems(data || []);
  }

  useEffect(() => {
    load();

    const chan = supabase
      .channel("pings-inserts")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "pings" }, (payload) => {
        const row = payload.new;
        setItems((prev) => [row, ...prev].slice(0, limit));
      })
      .subscribe((s) => {
        setStatus(`Realtime: ${s}`);
      });

    return () => {
      supabase.removeChannel(chan);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  const groups = useMemo(() => groupByDay(items), [items]);

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between">
        <div className="text-base font-black tracking-tight">{title}</div>
        <button
          className="rounded-full border border-line2 bg-transparent px-3 py-1 text-xs font-black text-white/80 hover:bg-white/5"
          onClick={load}
        >
          Refresh
        </button>
      </div>

      <div className="text-xs text-white/55">{status || "\u00A0"}</div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-line2 bg-white/5 p-4 text-sm text-white/70">
          No pings yet. Send one above 🦦
        </div>
      ) : (
        <div className="grid gap-3">
          {groups.map(([day, rows]) => (
            <div key={day} className="grid gap-2">
              <div className="text-xs font-black text-white/70">{day}</div>

              <div className="grid gap-2">
                {rows.map((p) => {
                  const mine = meId && p.sender_id === meId;
                  const senderName = mine ? (myLabel || "Me") : (partnerLabel || "Partner");

                  return (
                    <div
                      key={p.id}
                      className={cx(
                        "rounded-2xl border border-line2 bg-white/5 p-3",
                        mine ? "ring-1 ring-otter/15" : ""
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-black tracking-tight">{senderName}</div>
                          <div className="text-xs text-white/60">{fmtTime(p.created_at)}</div>
                        </div>
                        <div className="rounded-full border border-line2 bg-black/10 px-3 py-1 text-xs font-black text-white/80">
                          {kindNice(p.kind)}
                        </div>
                      </div>

                      {p.note ? (
                        <div className={cx("mt-2 text-sm text-white/85", compact ? "line-clamp-2" : "")}>
                          {p.note}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
